import SurveyResponseOffcanvas from "../../surveys/SurveyResponseOffcanvas";
import React, { useCallback, useContext, useEffect, useMemo, useState, memo, useRef } from "react";
import moment from "moment";
import { Image } from "uikit-react";
import UIkit from "uikit";
import { CSVLink } from "react-csv";
import clsx from "clsx";

import { AppContexts } from "../../../providers";
import { Link } from "../../../ui";
import TableLoading from "../../common/Loading/table_loading";
import { renderScheduledMessageBody } from "../../../utils/macros";
import SeamMonitoringPanel from "../../seam_monitoring/MonitoringPanel";
import { usePermissionsStore } from "../../permissions/permissions_store";
import IdentityVerificationBadge from "../../identity_verification/IdentityVerificationBadge";
import useIdentityVerificationStatuses from "../../identity_verification/useIdentityVerificationStatuses";

const WEEKDAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

const TimelineRow = memo(function TimelineRow({
  item,
  id,
  isOpen,
  onToggle,
  canSend,
  isDisabled,
  isSending,
  onSend,
  statusIcon,
  statusIconTooltip,
  metaLabel,
  metaValue,
  typeIcon,
  openDetail,
}) {
  const templateId = item._templateId;

  return (
    <div className="timeline-row">
      <div className="timeline-dot">
        <div className="timeline-circle">{typeIcon}</div>
      </div>

      <div className="timeline-content">
        <div className="timeline-meta">
          <img
            className="timeline-status-icon"
            src={statusIcon}
            alt="status"
            uk-tooltip={statusIconTooltip || undefined}
          />
          <div className="timeline-datetime">
            {metaLabel ? (
              <>
                {metaLabel}: {metaValue}
              </>
            ) : (
              <>{metaValue}</>
            )}
          </div>
        </div>

        <div className="timeline-title">
          <div
            className="semi-bold-font"
            style={{ cursor: item._detailRaw ? "pointer" : "default" }}
            onClick={() => item._detailRaw && onToggle(id)}
            uk-tooltip={item._detailRaw ? "Click to expand/collapse details" : undefined}
          >
            {item.name}
            {item._detailRaw ? (
              <span className="uk-margin-vsmall-left" uk-icon={isOpen ? "chevron-up" : "chevron-down"} />
            ) : null}
          </div>

          {canSend && (
            <div className="timeline-action">
              <div
                className={clsx("hms-sms-send-text", { disabled: isDisabled })}
                onClick={() => {
                  if (isDisabled) return;
                  onSend(templateId);
                }}
              >
                {isSending ? (
                  "Sending..."
                ) : item._sent ? (
                  <img src="/images/resend.svg" alt="Resend Message" uk-tooltip="Resend Message" />
                ) : (
                  <img src="/images/sms.svg" alt="Send Message Now" uk-tooltip="Send Message Now" />
                )}
              </div>
            </div>
          )}
        </div>

        {isOpen && openDetail ? <div className="timeline-detail">{openDetail}</div> : null}
      </div>
    </div>
  );
});

const GuestDetail = ({ canMessageGuests: canMessageGuestsProp }) => {
  const permissionsStore = usePermissionsStore();
  const canMessageGuests =
    typeof canMessageGuestsProp === "boolean"
      ? canMessageGuestsProp
      : permissionsStore.canEdit("messaging.guests_message");
  const {
    reservation,
    historyReservation,
    guestBook,
    survey,
    flagIsLoading,
    surveyResponse,
    setSurveyResponse,
    getSurveyResponse,
    gettingSurveyResponse,
    fivestar,
    sentScheduledMessages,
    sendingMsg,
    sendScheduledMessage,
    listReservations,
    setReservations,
    setSentScheduledMessages,
  } = useContext(AppContexts.ReservationsContext);

  const { threadDetail, selectPaginatedThread } = useContext(AppContexts.InboxContext);
  const { scheduledMsgs } = useContext(AppContexts.SchedulerContext);
  const { settings, get } = useContext(AppContexts.SettingsContext);

  const didFetchMacros = useRef(false);

  useEffect(() => {
    if (didFetchMacros.current) return;
    didFetchMacros.current = true;
    get(["custom_macros", "integration"]);
  }, [get]);

  const { getRental, rental } = useContext(AppContexts.RentalsContext);

  const lastRentalIdRef = useRef(null);
  const lastThreadIdRef = useRef(null);
  const timersRef = useRef({});

  // reset dedupe when switching reservations
  useEffect(() => {
    lastRentalIdRef.current = null;
    lastThreadIdRef.current = null;
    timersRef.current = {};
  }, [reservation?.guest_reservation_id]);

  useEffect(() => {
    const id = reservation?.rental_id;
    if (!id || !getRental) return;

    if (lastRentalIdRef.current === id) return;
    lastRentalIdRef.current = id;

    const key = `getRental:${id}`;
    if (timersRef.current[key]) return;
    timersRef.current[key] = true;

    Promise.resolve(getRental(id)).finally(() => {
      console.timeEnd(key);
      timersRef.current[key] = false;
    });
  }, [reservation?.rental_id, getRental]);

  useEffect(() => {
    const id = reservation?.thread_id ? +reservation.thread_id : null;
    if (!id || !selectPaginatedThread) return;

    if (lastThreadIdRef.current === id) return;
    lastThreadIdRef.current = id;

    const key = `selectPaginatedThread:${id}`;
    if (timersRef.current[key]) return;
    timersRef.current[key] = true;

    Promise.resolve(selectPaginatedThread(id, 0)).finally(() => {
      console.timeEnd(key);
      timersRef.current[key] = false;
    });
  }, [reservation?.thread_id, selectPaginatedThread]);

  const [isMsg, setTextMsg] = useState(false);
  const [surveyResponseOpen, setSurveyResponseOpen] = useState(false);
  const [sentOverride, setSentOverride] = useState({});

  const headers = [
    { label: "SENT AT", key: "created_at" },
    { label: "SENT BY", key: "sender_type" },
    { label: "MESSAGE", key: "content" },
  ];

  const sentKey = useCallback(
    (reservationId, templateId) => `${reservationId}:${templateId}`,
    []
  );

  const handleViewSurveyResponse = async (survey) => {
    setSurveyResponse();
    setSurveyResponseOpen(true);
    await getSurveyResponse(survey.survey_id, survey.survey_results_id);
  };

  const handleBackSurvey = () => {
    setSurveyResponseOpen(false);
    setSurveyResponse();
  };

  const handleSendScheduledMessage = useCallback(async (templateId) => {
    const res = await sendScheduledMessage(reservation.guest_reservation_id, templateId);

    if (res?.status) {
      const createdAt = moment.utc(res.status.created_at).local().format("MM/DD/YYYY hh:mm A");

      const status = {
        ...res.status,
        created_at: createdAt,
        guest_reservation_id: res.status.guest_reservation_id || reservation.guest_reservation_id,
        scheduled_message_id: res.status.scheduled_message_id || templateId,
        msg_id: res.status.msg_id || templateId,
      };

      setSentOverride((prev) => ({
        ...prev,
        [sentKey(reservation.guest_reservation_id, templateId)]: status,
      }));

      if (listReservations?.length) {
        setReservations(
          listReservations.map((x) =>
            +x.guest_reservation_id === +status.guest_reservation_id
              ? { ...x, sent_scheduled_messages: [...(x.sent_scheduled_messages || []), status] }
              : x
          )
        );
      }

      setSentScheduledMessages?.((prev) => {
        const next = [...(prev || []), status];
        const seen = new Set();
        return next.filter((x) => {
          const k = `${x.guest_reservation_id || ""}:${x.scheduled_message_id || x.msg_id || x.id}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      });

      UIkit.notification("Scheduled message sent successfully", "success");
    } else {
      UIkit.notification(res?.error || "Failed to send scheduled message", "danger");
    }
  }, [
    sendScheduledMessage,
    reservation?.guest_reservation_id,
    listReservations,
    setReservations,
    setSentScheduledMessages,
    sentKey,
  ]);

  const [openMsgId, setOpenMsgId] = useState(null);

  const toggleMsg = useCallback((id) => {
    setOpenMsgId((prev) => (prev === id ? null : id));
  }, []);

  const getMsgId = useCallback((item, i) => {
    return String(item?.scheduled_message_id ?? item?.id ?? `${i}-${item?.name ?? "msg"}`);
  }, []);

  const showMsgForReservation = useCallback((msg, reservation) => {
    if (!msg?.active || msg?.deleted) return false;

    const msgTagIds = (msg?.tags || [])
      .map(t => (t?.id ?? t))
      .filter(Boolean)
      .map(String);

    const resTagIds = (reservation?.tags || reservation?.tag_array || [])
      .map(t => (t?.id ?? t))
      .filter(Boolean)
      .map(String);

    if (msgTagIds.length > 0) {
      return msgTagIds.some(id => resTagIds.includes(id));
    }

    return true;
  }, []);

  const formatScheduledDisplay = (m) => {
    if (!m || !m.isValid?.() || m.year() < 2000) return null;
    return `${m.format("ddd")}, ${m.format("MMMM D, h:mm A")}`;
  };

  const getTimelineMeta = (item) => {
    const now = moment();
    const scheduledAt =
      item._scheduledAt && item._scheduledAt.isValid?.()
        ? item._scheduledAt
        : null;

    // Sent
    if (item._sent && item._sentAtLabel) {
      return { metaLabel: "Sent at", metaValue: item._sentAtLabel, state: "sent" };
    }

    // Unscheduled
    if (item._isUnscheduled) {
      return { metaLabel: "", metaValue: "Unscheduled", state: "unscheduled" };
    }

    // Weekday-based message (Trash day etc.)
    if (item._isWeekday && item._weekdayLabel) {
      return {
        metaLabel: "Scheduled for",
        metaValue: item._weekdayLabel,
        state: "upcoming",
      };
    }

    // Invalid or missing date → fallback to rule
    if (!scheduledAt || scheduledAt.year() < 2000) {
      return {
        metaLabel: "Scheduled for",
        metaValue: item._ruleLabel || "Unknown",
        state: "unknown",
      };
    }

    const formatted = formatScheduledDisplay(scheduledAt);

    // Missed
    if (scheduledAt.isBefore(now, "minute")) {
      return {
        metaLabel: "Was scheduled for",
        metaValue: formatted,
        state: "missed",
      };
    }

    // Upcoming
    return {
      metaLabel: "Scheduled for",
      metaValue: formatted,
      state: "upcoming",
    };
  };

  const buildStatusTooltip = (state, scheduledAtLabel) => {
    if (state === "missed") {
      return `This message was scheduled for ${scheduledAtLabel}, but it has not been sent yet.`;
    }
    return "";
  };  

  const scheduledList = useMemo(() => {
    return (scheduledMsgs || [])
      .filter((m) => m.active && !m.deleted)
      .filter((m) => showMsgForReservation(m, reservation));
  }, [scheduledMsgs, reservation, showMsgForReservation]);

  const parseWeekday = (item) => {
    // Adapt this to your actual data shape.
    // Possibilities:
    // item.weekday = "Wednesday"
    // item.day_of_week = 3
    // item.reference_point = "Wednesday"
    const w =
      item?.weekday ||
      item?.day_of_week ||
      item?.dayOfWeek ||
      item?.reference_point_weekday ||
      null;

    if (w == null) return null;

    if (typeof w === "number") return (w % 7); // 0=Sun ... 6=Sat
    const idx = WEEKDAYS.indexOf(String(w).trim().toLowerCase());
    return idx >= 0 ? idx : null;
  };

  const nextWeekdayFrom = (baseMoment, weekdayIndex) => {
    const base = baseMoment.clone().startOf("day");
    const delta = (weekdayIndex - base.day() + 7) % 7;
    return base.add(delta === 0 ? 7 : delta, "days"); // always “upcoming”, not “today”
  };

  const getTemplateId = useCallback((item) => {
    // scheduled templates might use any of these field names
    return (
      item?.scheduled_message_id ??
      item?.scheduledMsgId ??
      item?.msg_id ??
      item?.message_id ??
      item?.id ??
      null
    );
  }, []);  

  const getSentRecord = useCallback(
    (guest_reservation_id, itemOrId) => {
      const sentList = [
        ...(reservation?.sent_scheduled_messages || []),
        ...(sentScheduledMessages || []),
      ];

      if (!sentList.length) return null;

      const templateId =
        typeof itemOrId === "object" ? getTemplateId(itemOrId) : itemOrId;

      if (!templateId) return null;

      return (
        sentList.find((x) => {
          const sameReservation =
            !x.guest_reservation_id ||
            +x.guest_reservation_id === +guest_reservation_id;

          const xTemplateId =
            x?.msg_id ??
            x?.scheduled_message_id ??
            x?.scheduledMsgId ??
            x?.message_id ??
            x?.id;

          return sameReservation && +xTemplateId === +templateId;
        }) || null
      );
    },
    [reservation?.sent_scheduled_messages, sentScheduledMessages, getTemplateId],
  );

  const parseResDate = (val) => {
    if (!val) return moment.invalid();
    const m = moment(val, "YYYY-MM-DD");
    return m.isValid() ? m : moment.invalid();
  };

  const checkIn = useMemo(() => parseResDate(reservation?.check_in), [reservation?.check_in]);
  const checkOut = useMemo(() => parseResDate(reservation?.check_out), [reservation?.check_out]);

  const computeScheduledAt = useCallback((item) => {
    if (!checkIn?.isValid?.()) return null;

    const raw = item?.send_day;
    if (raw === undefined || raw === null) return null;

    const weekdayIndex = parseWeekday(item);
    if (weekdayIndex != null) {
      let base = moment();
      const refPoint = String(item?.reference_point || "").toLowerCase();
      if (refPoint.includes("out") && checkOut?.isValid?.()) base = checkOut.clone();
      else if (checkIn?.isValid?.()) base = checkIn.clone();

      const next = nextWeekdayFrom(base, weekdayIndex);

      const { hour, minute } = parseSendTime(item?.send_time);
      return next.hour(hour).minute(minute).second(0);
    }
    const send_day = Math.abs(+raw);
    if (send_day === 99999) return null;

    let anchor = checkIn;
    const refPoint = String(item?.reference_point || "").toLowerCase();
    if (refPoint.includes("out") && checkOut?.isValid?.()) anchor = checkOut;

    const { hour, minute } = parseSendTime(item?.send_time);
    anchor = anchor.clone().hour(hour).minute(minute).second(0);

    const dir = String(item?.reference_direction || "").toLowerCase();
    const isBefore = dir.includes("before");

    return isBefore ? anchor.clone().subtract(send_day, "days") : anchor.clone().add(send_day, "days");
  }, [checkIn, checkOut]);  

  const normalizeWeekdayLabel = (value) => {
    if (!value) return value;

    const s = String(value).trim().toLowerCase();
    const idx = WEEKDAYS.indexOf(s);

    if (idx === -1) return value;

    return `${WEEKDAYS[idx].slice(0, 3).charAt(0).toUpperCase()}${WEEKDAYS[idx].slice(1, 3)},`;
  };

  const formatWeekdayTimeLabel = (weekdayIndex, send_time) => {
    const { hour, minute } = parseSendTime(send_time);
    const time = moment().hour(hour).minute(minute).format("h:mm A");
    const day = weekdayAbbrevComma(weekdayIndex);
    return `${day} ${time}`;
  };

  const weekdayAbbrevComma = (value) => {
    if (value == null) return "";

    // if numeric weekday (0-6)
    if (typeof value === "number" && Number.isFinite(value)) {
      const d = WEEKDAYS[value % 7];
      return `${d.slice(0, 1).toUpperCase()}${d.slice(1, 3)},`;
    }

    const s = String(value).trim().toLowerCase();
    const idx = WEEKDAYS.indexOf(s);
    if (idx === -1) return String(value).trim(); // leave as-is

    const d = WEEKDAYS[idx];
    return `${d.slice(0, 1).toUpperCase()}${d.slice(1, 3)},`;
  };

  const weekdayAbbrevNoComma = (value) => {
    const w = weekdayAbbrevComma(value);
    return w.endsWith(",") ? w.slice(0, -1) : w;
  };

  const getDayString = useCallback((msg) => {
    let send_day = Math.abs(+msg.send_day);

    if (send_day === 99999) return "Unscheduled";
    if (send_day === 99998) return `Email Upon Booking at ${msg.send_time}`;

    const dir = msg.reference_direction
      ?.toLowerCase()
      .replace(/\b\w/g, (s) => s.toUpperCase());

    const time = msg.send_time;

    let ref = msg.reference_point || "";
    ref = weekdayAbbrevComma(ref) || ref; // "Wed," if weekday

    const joinRefTime = (r, t) => (String(r).trim().endsWith(",") ? `${r} ${t}` : `${r}, ${t}`);

    if (send_day <= 99997 && send_day >= 99990) {
      return `${99997 - send_day} Day(s) ${dir} ${joinRefTime(ref, time)}`;
    }

    if (send_day <= 99929 && send_day >= 99900) {
      return `${Math.abs(99900 - send_day)} Day(s) ${dir} ${joinRefTime(ref, time)}`;
    }

    return `${send_day} Day(s) ${dir} ${joinRefTime(ref, time)}`;
  }, []);

  function parseSendTime(send_time) {
    if (!send_time) return { hour: 15, minute: 0 }; // default 3pm

    const t = String(send_time).trim();

    // "15:00" or "15:00:00"
    const m24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) return { hour: +m24[1], minute: +m24[2] };

    // "3:00 PM" / "3 PM"
    const m12 = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (m12) {
      let hour = +m12[1];
      const minute = m12[2] ? +m12[2] : 0;
      const mer = m12[3].toUpperCase();
      if (mer === "PM" && hour !== 12) hour += 12;
      if (mer === "AM" && hour === 12) hour = 0;
      return { hour, minute };
    }

    return { hour: 15, minute: 0 };
  }

  const getMsgType = useCallback((item) => {
    if (item?.sms === true) return "sms";
    if (item?.email === true) return "email";
    return "message";
  }, []);

  const timeline = useMemo(() => {
    const list = (scheduledList || []).map((item, idx) => {
      const templateId = getTemplateId(item);
      const key = sentKey(reservation?.guest_reservation_id, templateId);
      const override = sentOverride[key];

      const sent = override || getSentRecord(reservation?.guest_reservation_id, item);

      const scheduledAt = computeScheduledAt(item);

      const isUnscheduled =
        !scheduledAt ||
        !scheduledAt.isValid?.() ||
        String(getDayString(item)).toLowerCase().includes("unscheduled");

      const type = getMsgType(item);

      const detailRaw =
        item?.body || item?.message || item?.content || item?.text ||
        item?.email_body || item?.sms_body || "";

      return {
        ...item,
        _idx: idx,
        _templateId: getTemplateId(item),
        _type: type,
        _detailRaw: detailRaw,
        _isUnscheduled: isUnscheduled,
        _scheduledAt: scheduledAt,
        _ruleLabel: getDayString(item),
        _scheduledAtLabel:
          scheduledAt && scheduledAt.isValid()
            ? scheduledAt.format("MMM D, YYYY h:mm a")
            : getDayString(item),
        _sent: !!sent,
        _sentAt: sent?.created_at || null,
        _sentAtLabel: sent?.created_at
          ? moment.utc(sent.created_at).local().format("ddd, MMM D, YYYY h:mm A")
          : "",
      };
    });

    //.local().format("MM/DD/YYYY hh:mm A")

    // scheduled first (sorted), unscheduled last
    const scheduledItems = list
      .filter((x) => !x._isUnscheduled)
      .sort((a, b) => (+a._scheduledAt || 0) - (+b._scheduledAt || 0));

    const unscheduledItems = list
      .filter((x) => x._isUnscheduled)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return [...scheduledItems, ...unscheduledItems];
  }, [
    scheduledList,
    reservation?.guest_reservation_id,
    sentOverride,
    sentKey,
    getSentRecord,
    computeScheduledAt,
    getDayString,
    getMsgType,
    getTemplateId,
  ]);

  const openItem = useMemo(() => {
    if (!openMsgId) return null;
    return timeline.find((it, idx) => getMsgId(it, idx) === openMsgId) || null;
  }, [timeline, openMsgId, getMsgId]);

  const guestlinkId = reservation?.guestlink_id;

  const reservationForMacros = useMemo(
    () => (reservation ? { ...reservation } : {}),
    [reservation]
  );

  const rentalForMacros = useMemo(
    () => (rental || reservation?.rental || {}),
    [rental, reservation?.rental]
  );

  const settingsForMacros = useMemo(
    () => (settings ? { ...settings } : {}),
    [settings]
  );

  const openDetail = useMemo(() => {
    if (!openItem?._detailRaw) return null;

    const body = renderScheduledMessageBody({
      template: openItem._detailRaw,
      reservation: reservationForMacros,
      rental: rentalForMacros,
      settings: settingsForMacros,
      guestlink: guestlinkId,
    });

    return (
      <div>
        <div>{body}</div>

        {openItem?._ruleLabel ? (
          <div className="timeline-rule hms-small-note uk-margin-small-top">
            Schedule: {openItem._ruleLabel}
          </div>
        ) : null}
      </div>
    );
  }, [
    openItem?._detailRaw,
    openItem?._ruleLabel,
    reservationForMacros,
    rentalForMacros,
    settingsForMacros,
    guestlinkId,
  ]);

  useEffect(() => {
    const handler = (e) => {
      setTextMsg(UIkit.util.index(e.target) === 2);
    };

    UIkit.util.on("#guest-detail", "shown", handler);
    return () => {
      UIkit.util.off("#guest-detail", "shown", handler);
    };
  }, []);

  const reservations = useMemo(
    () =>
      reservation.thread_id
        ? threadDetail.guest_reservations
        : historyReservation,
    [threadDetail, historyReservation, reservation.thread_id],
  );

  const messages = useMemo(() => {
    if (reservation.thread_id) {
      return threadDetail?.messages;
    }
    return [];
  }, [threadDetail, reservation.thread_id]);

  const identityStatuses = useIdentityVerificationStatuses(reservations);

  const sorted_messages = useMemo(() => {
    if (!isMsg) return [];
    return [...(messages || [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [messages, isMsg]);

  useEffect(() => {
    setOpenMsgId(null);
    setSurveyResponseOpen(false);
  }, [reservation?.guest_reservation_id]);
  
  const renderTypeIcon = (item) => {
    if (item._type === "sms") {
      return (
        <div className="guest-schedule-icon" uk-tooltip="Text Message">
          <Image src="/images/guest-detail-text.svg" />
        </div>
      );
    }
    if (item._type === "email") {
      return (
        <div className="guest-schedule-icon" uk-tooltip="Email Message">
          <Image src="/images/guest-detail-email.svg" />
        </div>
      );
    }
    return null;
  };

  const reservationRenderer = (reservation, index) => {
    return (
      <tr key={reservation?.guest_reservation_id ?? reservation?.id ?? `reservation_${index}`}>
        <td>
          <div>{`${moment
            .utc(reservation?.check_in)
            .local()
            .format("MMM DD, YYYY")} - ${moment
            .utc(reservation?.check_out)
            .local()
            .format("MMM DD, YYYY")}`}</div>
          <div className="identity-verification-detail-badge-row">
            <IdentityVerificationBadge
              reservation={reservation}
              lookup={identityStatuses.lookup(reservation.guest_reservation_id)}
            />
          </div>
        </td>
        <td>{reservation?.pms_id}</td>
        <td>
          {moment
            .utc(reservation?.created_at)
            .local()
            .format("MM/DD/YYYY hh:mm A")}
        </td>
      </tr>
    );
  };

  const messageRenderer = (message, index) => {
    return (
      <tr key={message?.id ?? message?.message_id ?? message?.created_at ?? `message_${index}`}>
        <td>
          {moment.utc(message.created_at).local().format("MM/DD/YYYY hh:mm A")}
          {message.status === "undelivered" ? (
            <>
              <img
                className="report-warning-icon"
                data-src="/images/warning.svg"
                data-uk-img=""
                loading="lazy"
                src="/images/warning.svg"
                alt="Warning Icon"
                data-uk-tooltip={`title: ${message.status} - ${message.error_code} - ${message.error_message}`}
              />
            </>
          ) : (
            <span></span>
          )}
        </td>
        <td>{message.sender_type}</td>
        <td>
          <div className="message-log-content">
            {message.content}
          </div>
          {(message.attachments || []).map((it, idx) => (
            <img
              key={it.id ?? it.url ?? `att-${message.id ?? message.created_at}-${idx}`}
              src={it.url}
              alt="Attachment"
            />
          ))}
        </td>
      </tr>
    );
  };

  const surveyRenderer = (survey, index) => {
    return (
      <tr key={`survey_${survey.survey_results_id}`}>
        <td>
          {moment.utc(survey.submitted_at).local().format("MM/DD/YYYY hh:mm A")}
        </td>
        <td>{moment.utc(reservation.check_in).local().format("MM/DD/YYYY")}</td>
        <td>{reservation.pms_id}</td>
        <td>{survey.name}</td>
        <td>
          <button type="button" className="uk-button hms-img-btn" aria-label="View response" uk-tooltip="View response" onClick={() => handleViewSurveyResponse(survey)}><img src="/images/detail.svg" alt="" /></button>
        </td>
      </tr>
    );
  };

  const guestBookRenderer = (guestBook, index) => {
    return (
      <tr key={`guestBook_${index}`}>
        <td>
          {moment.utc(guestBook.viewed_at).local().format("MM/DD/YYYY hh:mm A")}
        </td>
        <td>{guestBook.property_pms_id}</td>
        <td>
          <a href={`rental/${guestBook.property_id}`}>
            {guestBook.property_name}
          </a>
        </td>
      </tr>
    );
  };

  const guestDetailRef = useRef(null);

  useEffect(() => {
    const el = guestDetailRef.current;
    if (!el) return;

    const handler = (e) => {
      setTextMsg(UIkit.util.index(e.target) === 2);
    };

    UIkit.util.off(el, "shown", handler);
    UIkit.util.on(el, "shown", handler);

    return () => UIkit.util.off(el, "shown", handler);
  }, []);

  const fivestarRenderer = (rating, index) => {
    const key =
      rating?.id ??
      rating?.fivestar_id ??
      rating?.rated_on ??
      `${rating?.guest_name}-${rating?.check_in}-${index}`;

    return (
      <tr key={key}>
        <td>
          {moment.utc(rating.rated_on).local().format("MM/DD/YYYY hh:mm A")}
        </td>
        <td>{rating.guest_name}</td>
        <td>{moment.utc(rating.check_in).local().format("MM/DD/YYYY")}</td>
        <td className="reports-table-wrap-overflow">{rating.pms_id}</td>
        <td>
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-review">
              {[...Array(Math.round(rating.response))].map((value, idx) => (
                <span
                  key={`rating_${idx}`}
                  uk-icon="icon: star; ratio: 0.8"
                  className="hms-rated-star"
                ></span>
              ))}
              {[...Array(5 - Math.round(rating.response))].map((value, idx) => (
                <span
                  key={`non-rating_${idx}`}
                  uk-icon="icon: star; ratio: 0.8"
                ></span>
              ))}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      id="modal-guest-detail"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            data-uk-close
          />
          <div className="uk-modal-header">
            <span className="uk-modal-title">{reservation?.name}</span>
            <span className="uk-margin-small-left">
              {reservation?.formatted_phone}
            </span>
          </div>
          <div className="uk-modal-body">
            <div className="detail-header">
              <ul
                data-uk-tab
                uk-tab="connect: #guest-detail; animation: uk-animation-fade"
                className="uk-margin-small-bottom uk-margin-small-top"
              >
                <li className="uk-active">
                  <Link href="#">Guest Journey</Link>
                </li>
                <li>
                  <Link href="#">Stays</Link>
                </li>
                <li>
                  <Link href="#">Message Logs</Link>
                </li>
                <li>
                  <Link href="#">Ratings</Link>
                </li>
                <li>
                  <Link href="#">Surveys</Link>
                </li>
                <li>
                  <Link href="#">Guestbook Views</Link>
                </li>
                <li>
                  <Link href="#">Smart Lock Logs</Link>
                </li>
              </ul>

              {isMsg && (sorted_messages || []).length > 0 && (
                <CSVLink
                  data={sorted_messages}
                  filename={`${moment().unix()}_msg_logs.csv`}
                  headers={headers}
                >
                  <button className="download">
                    <Image
                      src="/images/download.svg"
                      uk-tooltip="Download CSV"
                    />
                  </button>
                </CSVLink>
              )}
            </div>
            <div>
              <ul id="guest-detail" ref={guestDetailRef} className="uk-switcher">
                <li>
                  <div className="guest-journey uk-padding-small">
                    {flagIsLoading ? (
                      <div>{TableLoading(1)}</div>
                    ) : (
                      <div className="guest-timeline">
                        <div className="timeline-row timeline-anchor">
                          <div className="timeline-dot">
                            <div className="timeline-circle">
                              <img src="/images/guest-detail-check-in.svg" alt="checked in" />
                            </div>
                          </div>

                          <div className="timeline-content">
                            <div className="timeline-datetime">
                              {moment(reservation?.check_in, "YYYY-MM-DD").format("MMM D, YYYY")}
                            </div>
                            <div className="timeline-title">
                              <div className="semi-bold-font">Check-In</div>
                            </div>
                          </div>
                        </div>

                        {timeline.map((item, idx) => {
                          const id = getMsgId(item, idx);
                          const isOpen = openMsgId === id;

                          const isDisabled =
                            !canMessageGuests ||
                            (item.email ? !reservation.email : !reservation.phone);

                          const isSending =
                            +sendingMsg?.reservation_id === +reservation.guest_reservation_id &&
                            +sendingMsg?.msg_id === +(item._templateId ?? getTemplateId(item));

                          const missing =
                            (item.sms === true && !reservation.phone) ||
                            (item.email === true && !reservation.email);

                          const { metaLabel, metaValue, state } = getTimelineMeta(item);

                          const statusIcon = missing
                            ? "/images/missing-phone-email.svg"
                            : state === "sent"
                              ? "/images/check.svg"
                              : state === "missed"
                                ? "/images/warning.svg"
                                : "/images/send.svg";

                          const statusIconTooltip =
                            missing
                              ? "Missing phone/email for this message"
                              : buildStatusTooltip(state, metaValue);

                          return (
                            <TimelineRow
                              key={id}
                              item={item}
                              id={id}
                              isOpen={isOpen}
                              onToggle={toggleMsg}
                              canSend={canMessageGuests}
                              isDisabled={isDisabled}
                              isSending={isSending}
                              onSend={handleSendScheduledMessage}
                              statusIcon={statusIcon}
                              statusIconTooltip={statusIconTooltip}
                              metaLabel={metaLabel}
                              metaValue={metaValue}
                              typeIcon={renderTypeIcon(item)}
                              openDetail={isOpen ? openDetail : null}
                            />
                          );
                        })}                        

                        <div className="timeline-row timeline-anchor">
                          <div className="timeline-dot">
                            <div className="timeline-circle">
                              <img src="/images/guest-detail-check-out.svg" alt="checked out" />
                            </div>
                          </div>

                          <div className="timeline-content">
                            <div className="timeline-datetime">
                              {moment(reservation?.check_out, "YYYY-MM-DD").format("MMM D, YYYY")}
                            </div>
                            <div className="timeline-title">
                              <div className="semi-bold-font">Check-Out</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
                <li>  
                  <div className="guest-stays">
                    <table className="uk-table uk-table-hover uk-table-divider hms-table">
                      <thead>
                        <tr>
                          <th>CHECK-IN & OUT</th>
                          <th>PROPERTY ID</th>
                          <th>CREATED AT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flagIsLoading ? (
                          TableLoading(3)
                        ) : (reservations || []).length ? (
                          (reservations || []).map(reservationRenderer)
                        ) : (
                          <tr>
                            <td colSpan={3} className="no-records">
                              No reservations found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </li>
                <li>  
                  <div className="guest-messages">
                    <table className="uk-table uk-table-hover uk-table-divider hms-table">
                      <thead>
                        <tr>
                          <th>SENT AT</th>
                          <th>SENT BY</th>
                          <th>MESSAGE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flagIsLoading ? (
                          TableLoading(3)
                        ) : (sorted_messages || []).length ? (
                          (sorted_messages || []).map(messageRenderer)
                        ) : (
                          <tr>
                            <td colSpan={3} className="no-records">
                              No text messages sent or received.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </li>
                <li>  
                  <div className="guest-ratings">
                    <table className="uk-table uk-table-hover uk-table-divider hms-table uk-margin-small-top">
                      <thead>
                        <tr>
                          <th>RATED ON</th>
                          <th>GUEST NAME</th>
                          <th>CHECK-IN DATE</th>
                          <th>PROPERTY ID</th>
                          <th>RATING</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flagIsLoading ? (
                          TableLoading(5)
                        ) : (fivestar || []).length ? (
                          (fivestar || []).map(fivestarRenderer)
                        ) : (
                          <tr>
                            <td colSpan={5} className="no-records">
                              No ratings yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </li>
                <li>  
                  <div className="guest-surveys">
                    {(
                      <table className="uk-table uk-table-hover uk-table-divider hms-table">
                        <thead>
                          <tr>
                            <th>SUBMITTED AT</th>
                            <th>CHECK-IN Date</th>
                            <th>PROPERTY ID</th>
                            <th>SURVEY</th>
                            <th>RESPONSE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flagIsLoading ? (
                            TableLoading(5)
                          ) : (survey || []).length ? (
                            (survey || []).map(surveyRenderer)
                          ) : (
                            <tr>
                              <td colSpan={5} className="no-records">
                                No survey responses yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </li>
                <li>  
                  <div className="guest-bookview">
                    <table className="uk-table uk-table-hover uk-table-divider hms-table">
                      <thead>
                        <tr>
                          <th>VIEWED AT</th>
                          <th>PROPERTY ID</th>
                          <th>HOME GUIDE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flagIsLoading ? (
                          TableLoading(3)
                        ) : (guestBook || []).length ? (
                          (guestBook || []).map(guestBookRenderer)
                        ) : (
                          <tr>
                            <td colSpan={3} className="no-records">
                              No guestbook views yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <p className="hms-small-note">* Guestbook views for this booking only.</p>
                  </div>
                </li>
                <li>
                  <div className="guest-smart-lock-logs">
                    <SeamMonitoringPanel
                      showTitle={false}
                      rentalId={reservation?.rental_id}
                      guestReservationId={reservation?.guest_reservation_id}
                      defaultStartDate={reservation?.check_in}
                      defaultEndDate={reservation?.check_out}
                      showFilters={false}
                      showDevices={false}
                      showActivityDeviceName={false}
                      showActivityActor={false}
                      compact
                    />
                    <p className="hms-small-note">* Smart lock logs for this booking only.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <SurveyResponseOffcanvas open={surveyResponseOpen} survey={surveyResponse} variant="guest"
        result={surveyResponse?.survey_results?.[0]} loading={gettingSurveyResponse}
        onClose={handleBackSurvey} />
    </div>
  );
};

export default GuestDetail;
